/**
 * SocialCapital.cc — home / dashboard landing.
 *
 * Layout exactly matches the approved design mockup:
 *   • Branded top nav (SocialCapitalHeader)
 *   • Hero: multicolour headline + subhead + CTAs + illustration
 *   • KPI strip: 5 metric cards with trend delta
 *   • Main grid: Score-over-time chart · Insights & Trends · API panel
 *   • Footer strip: Help / Contact / Privacy / Terms
 *
 * When the visitor is signed in AND has a passport, the KPI + chart
 * pull live data. Otherwise we render sensible demo/aspirational values
 * so the landing still communicates the product story.
 */
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, PlayCircle, TrendingUp, HandHeart, Handshake, Users,
  Heart, LineChart as LineChartIcon, ChevronRight, Code2, Shield,
  LifeBuoy, Mail, Lock, FileText, Building2, Sparkles,
} from "lucide-react";

import SocialCapitalHeader, {
  BRAND_GREEN, BRAND_BLUE, BRAND_PURPLE,
} from "@/components/socialcapital/SocialCapitalHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sc`;

// ── KPI defs (labels + icons + colour). Values arrive live below. ──
const KPI_DEFS = [
  { key: "overall",    label: "Overall Social Capital Score",
    icon: Users,     tint: BRAND_PURPLE, tintBg: "rgba(124,58,237,0.10)",
    suffix: "/100", strong: "Strong" },
  { key: "community",  label: "Community Participation",
    icon: HandHeart, tint: BRAND_GREEN,  tintBg: "rgba(16,185,129,0.10)",
    suffix: "%",    strong: "Active" },
  { key: "trust",      label: "Trust & Reciprocity",
    icon: Handshake, tint: BRAND_BLUE,   tintBg: "rgba(59,130,246,0.10)",
    suffix: "%",    strong: "Strong" },
  { key: "networks",   label: "Networks & Connections",
    icon: Users,     tint: "#F59E0B",     tintBg: "rgba(245,158,11,0.10)",
    suffix: "%",    strong: "Moderate" },
  { key: "civic",      label: "Civic Engagement",
    icon: Heart,     tint: BRAND_PURPLE, tintBg: "rgba(124,58,237,0.10)",
    suffix: "%",    strong: "Moderate" },
];

// Demo values used until we have live insights for the visitor.
const DEMO_KPI = {
  overall:   { value: 72, delta: 8 },
  community: { value: 68, delta: 6 },
  trust:     { value: 71, delta: 7 },
  networks:  { value: 65, delta: 5 },
  civic:     { value: 59, delta: 4 },
};


// Aspirational demo chart data (Y in 0-100 range so it maps 1:1 to KPI).
const DEMO_CHART = [
  { m: "May", v: 43 },  { m: "Jun", v: 45 },  { m: "Jul", v: 48 },
  { m: "Aug", v: 46 },  { m: "Sep", v: 52 },  { m: "Oct", v: 55 },
  { m: "Nov", v: 51 },  { m: "Dec", v: 58 },  { m: "Jan", v: 62 },
  { m: "Feb", v: 65 },  { m: "Mar", v: 68 },  { m: "Apr", v: 72 },
];

const INSIGHTS = [
  { icon: TrendingUp, tint: BRAND_GREEN,
    title: "Trust is Growing",
    body: "Residents feel more trust in local institutions compared to last quarter." },
  { icon: HandHeart, tint: BRAND_BLUE,
    title: "Volunteerism on the Rise",
    body: "More residents are volunteering in community activities." },
  { icon: LineChartIcon, tint: "#F59E0B",
    title: "Room to Improve",
    body: "Civic engagement is moderate. Explore ways to increase participation." },
  { icon: Users, tint: BRAND_PURPLE,
    title: "Youth Engagement Opportunity",
    body: "Younger residents report lower connection levels. Consider youth-focused initiatives." },
];

const API_ACTIVITY = [
  { org: "Community Health Network", when: "2m ago" },
  { org: "United Way Alliance",       when: "1h ago" },
  { org: "Civic Insights Group",      when: "3h ago" },
];


export default function SocialCapitalHome() {
  const [passport, setPassport] = useState(null);

  useEffect(() => {
    axios.get(`${API}/me/passport`, { withCredentials: true })
      .then((r) => setPassport(r.data))
      .catch(() => setPassport(null));
  }, []);

  // Derive KPI values from the passport when available, otherwise use demo.
  const kpi = useMemo(() => {
    if (!passport || !passport.balance) return DEMO_KPI;
    const total = Number(passport.balance.total_points || 0);
    const acts  = Number(passport.balance.activity_count || 0);
    // Map raw points to a 0-100 scale via a squash function (asymptote at 100).
    const squash = (n) => Math.round(100 * (n / (n + 200)));
    const activityRatio = Math.min(100, acts * 5);
    return {
      overall:   { value: squash(total),                   delta: 0 },
      community: { value: activityRatio,                   delta: 0 },
      trust:     { value: squash(Math.floor(total * 1.05)),delta: 0 },
      networks:  { value: Math.min(100, acts * 4 + 20),    delta: 0 },
      civic:     { value: squash(Math.floor(total * 0.85)),delta: 0 },
    };
  }, [passport]);

  return (
    <div
      data-testid="sc-home"
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg,#F5F8FF 0%,#F8FAFC 100%)" }}
    >
      <Helmet>
        <title>SocialCapital.cc — Document what you do. Measure the good you create.</title>
        <meta
          name="description"
          content="Social Capital recognizes and protects the positive impact you create in your community and the world. Independent, append-only trust ledger."
        />
      </Helmet>

      <SocialCapitalHeader />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-10 pb-6">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_540px] items-start">
          <div data-testid="hero">
            <h1
              className="font-display leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-[52px]"
              style={{ color: "#0F172A" }}
            >
              Document What You Do.
              <br />
              Measure the Good You Create.
              <br />
              <span style={{ color: BRAND_BLUE }}>Build</span>{" "}
              <span style={{ color: "#0F172A" }}>a </span>
              <span style={{ color: BRAND_GREEN }}>Reputation.</span>{" "}
              <span style={{ color: BRAND_PURPLE }}>Leave a Legacy.</span>
            </h1>
            <p
              className="mt-5 text-[16px] md:text-[17px] max-w-[520px] leading-[1.55]"
              style={{ color: "#475569" }}
            >
              Social Capital recognizes and protects the positive impact you
              create in your community and the world.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/sc/me"
                data-testid="hero-cta-dashboard"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[14px] font-semibold text-white shadow-sm transition hover:-translate-y-0.5"
                style={{ background: BRAND_BLUE }}
              >
                View My Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/sc/how-it-works"
                data-testid="hero-cta-how"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[14px] font-semibold transition hover:-translate-y-0.5 bg-white border"
                style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
              >
                Explore How It Works <PlayCircle className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <HeroIllustration />
        </div>
      </section>

      {/* ── KPI Strip ────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div
          data-testid="kpi-strip"
          className="rounded-xl border bg-white shadow-sm grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x"
          style={{ borderColor: "#E5E7EB", borderColor: "#E5E7EB" }}
        >
          {KPI_DEFS.map((def) => {
            const v = kpi[def.key] || { value: 0, delta: 0 };
            return (
              <div
                key={def.key}
                data-testid={`kpi-${def.key}`}
                className="p-5 flex items-center gap-4"
                style={{ borderColor: "#E5E7EB" }}
              >
                <span
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0"
                  style={{ background: def.tintBg, color: def.tint }}
                >
                  <def.icon className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <div
                    className="text-[11.5px] leading-tight mb-1"
                    style={{ color: "#64748B" }}
                  >
                    {def.label}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div
                      className="font-display text-[28px] leading-none"
                      style={{ color: "#0F172A" }}
                    >
                      {v.value}
                      <span className="text-[15px] opacity-70">{def.suffix}</span>
                    </div>
                    {v.delta > 0 && (
                      <div
                        className="inline-flex items-center gap-0.5 text-[12px] font-semibold"
                        style={{ color: BRAND_GREEN }}
                      >
                        <TrendingUp className="w-3 h-3" /> {v.delta} pts
                      </div>
                    )}
                  </div>
                  <div
                    className="text-[12px] mt-0.5"
                    style={{ color: def.tint }}
                  >
                    {def.strong}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Chart · Insights · API grid ──────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6 grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <ScoreOverTimeCard />
        <InsightsCard />
        <ApiPanelCard />
      </section>

      {/* ── Footer strip ─────────────────────────────────────── */}
      <footer
        className="border-t mt-8"
        style={{ borderColor: "#E5E7EB", background: "white" }}
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px]">
          <div className="flex items-center gap-2 mr-auto" style={{ color: "#334155" }}>
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full"
              style={{ background: "rgba(59,130,246,0.10)", color: BRAND_BLUE }}
            >
              <Handshake className="w-3.5 h-3.5" />
            </span>
            <b>Social Capital is built by everyone.</b>
            <span style={{ color: "#64748B" }}>
              Together, we create stronger communities and a better world.
            </span>
          </div>
          <FooterLink to="/help" icon={LifeBuoy}>Help Center</FooterLink>
          <FooterLink to="/contact" icon={Mail}>Contact Us</FooterLink>
          <FooterLink to="/privacy" icon={Lock}>Privacy Policy</FooterLink>
          <FooterLink to="/terms" icon={FileText}>Terms of Use</FooterLink>
        </div>
      </footer>
    </div>
  );
}


// ─── Sub-components ────────────────────────────────────────────────

function FooterLink({ to, icon: Icon, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 hover:underline"
      style={{ color: "#334155" }}
    >
      <Icon className="w-3.5 h-3.5 opacity-70" /> {children}
    </Link>
  );
}


function HeroIllustration() {
  // Stylised community-in-nature scene. Green foreground, blue horizon.
  return (
    <div data-testid="hero-illustration" className="relative">
      {/* Handwritten note */}
      <div
        className="absolute -top-2 left-6 right-2 text-right z-10"
        style={{
          fontFamily: "'Caveat', 'Comic Sans MS', cursive",
          color: BRAND_PURPLE,
        }}
      >
        <div className="text-[26px] leading-tight" style={{ color: "#334155" }}>
          Stronger connections.
        </div>
        <div className="text-[26px] leading-tight italic" style={{ color: "#334155" }}>
          Stronger <span style={{ borderBottom: `2px solid ${BRAND_PURPLE}` }}>communities.</span>
        </div>
      </div>

      <svg
        viewBox="0 0 540 340"
        className="w-full h-auto rounded-xl"
        aria-hidden="true"
        style={{
          background: "linear-gradient(180deg,#EAF3FF 0%,#F6FFFA 60%,#EAF9F0 100%)",
        }}
      >
        {/* Distant city skyline */}
        <g opacity="0.28">
          <rect x="360" y="110" width="24" height="70" fill="#94A3B8" />
          <rect x="386" y="90"  width="18" height="90" fill="#64748B" />
          <rect x="406" y="70"  width="30" height="110" fill="#94A3B8" />
          <rect x="438" y="95"  width="20" height="85" fill="#64748B" />
          <rect x="460" y="120" width="26" height="60" fill="#94A3B8" />
          <rect x="488" y="100" width="20" height="80" fill="#64748B" />
          <rect x="510" y="115" width="22" height="65" fill="#94A3B8" />
        </g>

        {/* River */}
        <path
          d="M0 220 C 120 200 220 240 340 215 C 420 200 480 220 540 210 L 540 260 L 0 260 Z"
          fill="#BAD8F7" opacity="0.55"
        />
        <path
          d="M120 218 C 200 205 260 240 340 215"
          fill="none" stroke="#93C5FD" strokeWidth="1.5" opacity="0.7"
        />

        {/* Bridge */}
        <path d="M180 218 L 260 200 L 300 200 L 340 218" fill="none"
              stroke="#475569" strokeWidth="3" />
        <path d="M195 218 L 200 208" stroke="#475569" strokeWidth="1.2" />
        <path d="M215 218 L 220 204" stroke="#475569" strokeWidth="1.2" />
        <path d="M240 218 L 245 202" stroke="#475569" strokeWidth="1.2" />
        <path d="M270 218 L 275 200" stroke="#475569" strokeWidth="1.2" />
        <path d="M300 218 L 305 202" stroke="#475569" strokeWidth="1.2" />
        <path d="M325 218 L 330 208" stroke="#475569" strokeWidth="1.2" />

        {/* Hills */}
        <path d="M0 260 Q 120 210 260 240 T 540 235 L 540 340 L 0 340 Z" fill="#A7F3D0" />
        <path d="M0 285 Q 160 250 320 275 T 540 270 L 540 340 L 0 340 Z" fill="#6EE7B7" />

        {/* Left house */}
        <g>
          <polygon points="60,220 90,200 120,220" fill="#3B82F6" />
          <rect x="65" y="220" width="55" height="35" fill="white" stroke="#94A3B8" />
          <rect x="82" y="232" width="12" height="16" fill={BRAND_GREEN} />
          <rect x="100" y="228" width="10" height="10" fill="#BFDBFE" />
        </g>

        {/* Right houses */}
        <g>
          <polygon points="410,235 435,215 460,235" fill="#3B82F6" />
          <rect x="413" y="235" width="47" height="30" fill="white" stroke="#94A3B8" />
          <rect x="428" y="245" width="10" height="14" fill={BRAND_GREEN} />
        </g>
        <g>
          <polygon points="470,230 493,212 516,230" fill="#3B82F6" />
          <rect x="472" y="230" width="44" height="30" fill="white" stroke="#94A3B8" />
          <rect x="486" y="240" width="10" height="14" fill={BRAND_GREEN} />
        </g>

        {/* Trees */}
        {[
          [30, 240], [140, 250], [170, 245], [370, 250], [395, 265], [530, 250],
        ].map(([x, y], i) => (
          <g key={i}>
            <rect x={x - 1} y={y} width="3" height="14" fill="#78350F" />
            <circle cx={x} cy={y - 6} r="12" fill="#10B981" />
            <circle cx={x - 6} cy={y - 2} r="9" fill="#059669" />
            <circle cx={x + 6} cy={y - 2} r="9" fill="#059669" />
          </g>
        ))}

        {/* People (silhouettes) */}
        <g fill="#1E3A8A">
          {/* Walking pair on bridge */}
          <circle cx="245" cy="200" r="3" />
          <rect x="243" y="203" width="4" height="7" />
          <circle cx="255" cy="200" r="3" />
          <rect x="253" y="203" width="4" height="7" />
          {/* Cyclist */}
          <circle cx="330" cy="245" r="3" />
          <rect x="328" y="248" width="4" height="7" />
          <circle cx="332" cy="258" r="4" fill="none" stroke="#1E3A8A" strokeWidth="1" />
          {/* Family on grass */}
          <circle cx="470" cy="278" r="3" />
          <rect x="468" y="281" width="4" height="8" />
          <circle cx="478" cy="278" r="3" />
          <rect x="476" y="281" width="4" height="8" />
          <circle cx="486" cy="282" r="2" />
          <rect x="485" y="284" width="3" height="6" />
        </g>

        {/* Bench (right) */}
        <g stroke="#78350F" strokeWidth="1.5">
          <line x1="505" y1="278" x2="525" y2="278" />
          <line x1="507" y1="278" x2="507" y2="285" />
          <line x1="523" y1="278" x2="523" y2="285" />
        </g>

        {/* Sun */}
        <circle cx="90" cy="60" r="18" fill="#FDE68A" opacity="0.8" />
      </svg>
    </div>
  );
}


function ScoreOverTimeCard() {
  const data = DEMO_CHART;
  const min = Math.min(...data.map((d) => d.v));
  const max = Math.max(...data.map((d) => d.v));
  const pad = 6;
  const W = 460, H = 190;

  const xOf = (i) => 30 + (i * (W - 60)) / (data.length - 1);
  const yOf = (v) => pad + (H - 60) * (1 - (v - Math.max(0, min - 8)) / (max - Math.max(0, min - 8) + 5)) + 10;

  const linePath = data.map((d, i) =>
    `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yOf(d.v).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${xOf(data.length - 1).toFixed(1)} ${H - 20} L ${xOf(0).toFixed(1)} ${H - 20} Z`;
  const lastX = xOf(data.length - 1);
  const lastY = yOf(data[data.length - 1].v);

  return (
    <div
      data-testid="score-chart"
      className="rounded-xl border bg-white p-5 shadow-sm"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[17px]" style={{ color: "#0F172A" }}>
          Social Capital Score Over Time
        </h3>
        <select
          data-testid="chart-range"
          className="text-[12px] px-2 py-1 rounded-md border"
          style={{ borderColor: "#CBD5E1" }}
          defaultValue="12"
        >
          <option value="12">Last 12 Months</option>
          <option value="6">Last 6 Months</option>
          <option value="3">Last 3 Months</option>
        </select>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <linearGradient id="scArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={BRAND_PURPLE} stopOpacity="0.25" />
            <stop offset="100%" stopColor={BRAND_PURPLE} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Y ticks */}
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={t}>
            <line x1="24" x2={W - 10} y1={yOf(t)} y2={yOf(t)}
                   stroke="#F1F5F9" strokeWidth="1" />
            <text x="16" y={yOf(t) + 3} fontSize="9" textAnchor="end"
                   fill="#94A3B8">{t}</text>
          </g>
        ))}

        <path d={areaPath} fill="url(#scArea)" />
        <path d={linePath} fill="none" stroke={BRAND_PURPLE} strokeWidth="2.2" />

        {data.map((d, i) => (
          <circle key={d.m} cx={xOf(i)} cy={yOf(d.v)} r="3"
                   fill="white" stroke={BRAND_PURPLE} strokeWidth="1.5" />
        ))}

        {/* Latest value pill */}
        <g>
          <rect x={lastX - 16} y={lastY - 22} width="32" height="16" rx="4"
                 fill={BRAND_PURPLE} />
          <text x={lastX} y={lastY - 10} fontSize="10" textAnchor="middle"
                 fill="white" fontWeight="700">{data[data.length - 1].v}</text>
        </g>

        {/* X labels */}
        {data.map((d, i) => (
          <text key={d.m} x={xOf(i)} y={H - 4} fontSize="9"
                 textAnchor="middle" fill="#94A3B8">{d.m}</text>
        ))}
      </svg>

      <div className="flex items-center gap-2 mt-3 text-[12.5px]" style={{ color: "#64748B" }}>
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded-full"
          style={{ background: "rgba(16,185,129,0.10)", color: BRAND_GREEN }}
        >
          <Sparkles className="w-3 h-3" />
        </span>
        Your community's social capital is strengthening! Keep building connections.
      </div>
    </div>
  );
}


function InsightsCard() {
  return (
    <div
      data-testid="insights-card"
      className="rounded-xl border bg-white p-5 shadow-sm"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[17px]" style={{ color: "#0F172A" }}>
          Insights &amp; Trends
        </h3>
        <Link to="/sc/impact" className="text-[12px] font-semibold hover:underline"
              style={{ color: BRAND_BLUE }}>
          View All
        </Link>
      </div>
      <div className="divide-y" style={{ borderColor: "#F1F5F9" }}>
        {INSIGHTS.map((n) => (
          <button
            key={n.title}
            data-testid={`insight-${n.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="w-full flex items-start gap-3 py-3 text-left group"
          >
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0"
              style={{ background: "rgba(16,185,129,0.08)", color: n.tint }}
            >
              <n.icon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div
                className="text-[13.5px] font-semibold leading-tight"
                style={{ color: "#0F172A" }}
              >
                {n.title}
              </div>
              <div className="text-[12.5px] mt-1 leading-snug" style={{ color: "#64748B" }}>
                {n.body}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-80 mt-1" />
          </button>
        ))}
      </div>
    </div>
  );
}


function ApiPanelCard() {
  return (
    <div
      data-testid="api-panel"
      className="rounded-xl border bg-white p-5 shadow-sm"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[17px]" style={{ color: "#0F172A" }}>
          API &amp; Integrations
        </h3>
        <Link to="/sc/docs" className="text-[12px] font-semibold hover:underline"
              style={{ color: BRAND_BLUE }}>
          View Docs
        </Link>
      </div>

      <div
        className="rounded-lg p-4 mb-4 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg,#EEF4FF 0%,#F5F0FF 100%)",
          border: "1px solid #E0E7FF",
        }}
      >
        <div className="flex items-start gap-3">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md"
            style={{ background: "white", color: BRAND_BLUE }}
          >
            <Code2 className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold" style={{ color: "#0F172A" }}>
              SocialCapital Connect API
            </div>
            <div className="text-[12px] italic" style={{ color: "#64748B" }}>
              Secure. Scalable. Impactful.
            </div>
            <p
              className="text-[12.5px] mt-2 leading-snug"
              style={{ color: "#475569" }}
            >
              Integrate social capital data into your systems and empower
              your organization.
            </p>
            <Link
              to="/sc/docs"
              data-testid="explore-api-btn"
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full text-[12px] font-semibold text-white"
              style={{ background: BRAND_BLUE }}
            >
              Explore API <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <Shield className="w-7 h-7 shrink-0" style={{ color: BRAND_BLUE, opacity: 0.4 }} />
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[12.5px] font-semibold" style={{ color: "#0F172A" }}>
          Recent API Activity
        </div>
        <Link to="/sc/org" className="text-[11.5px] font-semibold hover:underline"
              style={{ color: BRAND_BLUE }}>
          View All
        </Link>
      </div>
      <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
        {API_ACTIVITY.map((a) => (
          <li key={a.org} className="flex items-center gap-2.5 py-2.5">
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
              style={{ background: "rgba(59,130,246,0.08)", color: BRAND_BLUE }}
            >
              <Building2 className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-medium truncate" style={{ color: "#0F172A" }}>
                {a.org}
              </div>
              <div className="text-[11px]" style={{ color: "#64748B" }}>
                Data retrieved successfully
              </div>
            </div>
            <div className="text-[11px] inline-flex items-center gap-1" style={{ color: BRAND_GREEN }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND_GREEN }} />
              {a.when}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
